import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Search,
  CheckCircle2,
  AlertCircle,
  Copy,
  RefreshCw,
  Info,
  PackageSearch,
  ScanBarcode,
  ShieldCheck,
} from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { FaqSection } from "@/components/seo/FaqSection";
import { toast } from "sonner";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import { validateUpcA, expandUpcE, upcANumberSystem, computeCheckDigit } from "@/lib/gtin";
import { usePrefillValue } from "@/lib/usePrefillValue";

const PATH = "/upc-lookup";
const TITLE = "UPC Lookup — Validate UPC-A & UPC-E Barcodes Online";
const DESC =
  "Free UPC tool. Validate UPC-A and UPC-E barcodes, verify the check digit, decode the number system, then link out to product catalogs — all in your browser.";

const FAQ = [
  {
    q: "What Is a UPC Code?",
    a: "UPC (Universal Product Code) is the 12-digit barcode used on most retail products sold in the United States and Canada. The full form is UPC-A; a compressed 8-digit variant called UPC-E exists for small packages.",
  },
  {
    q: "How is the UPC check digit calculated?",
    a: "Take the first 11 digits, multiply digits in odd positions (1st, 3rd, …) by 3 and even positions by 1, sum them, then the check digit is the value that makes the total a multiple of 10. ReadBarcode does this calculation for you and tells you the expected digit if it doesn't match.",
  },
  {
    q: "Why does my UPC fail validation?",
    a: "Common reasons: extra spaces or letters, missing or extra digits, or a typo that breaks the check digit. The error message will tell you which one. UPC-A must be exactly 12 numeric digits.",
  },
  {
    q: "Does This Tool Return Product Names and Brands?",
    a: "Not yet — this is a free validator and structure decoder. A real product database lookup is on the roadmap. For now, use the validated UPC with your own product source or the link-outs below.",
  },
  {
    q: "Can I Look up an EAN-13 Here?",
    a: "Use the EAN Lookup tool for EAN-8 and EAN-13. A UPC-A is technically an EAN-13 with a leading zero, so the GTIN Validator also accepts both.",
  },
  {
    q: "How Many Digits Is a UPC Code?",
    a: "A UPC-A has 12 digits: 11 digits of company prefix and item reference, then a check digit. A UPC-E prints 8 digits and is a zero-suppressed form of a UPC-A. The validator above also accepts a UPC-E typed as 6 or 7 digits, without the number system digit or the check digit.",
  },
  {
    q: "Is a UPC Unique?",
    a: "Yes. Under GS1 rules each UPC identifies one trade item, and the brand owner must not give the same number to a different product. Each size, flavor or pack count gets its own UPC. Codes in restricted ranges, such as number system 2 for variable-weight items or 4 for in-store use, are only unique inside one store or company.",
  },
  {
    q: "Can You Look up a UPC Code for Free?",
    a: "Yes. Validating and decoding a UPC on this page is free, needs no account and runs in your browser. The Google and Barcode Lookup buttons send the checked code to those external sites, which set their own coverage and terms.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Lookup & Validators", path: "/lookup-validators" },
  { name: "UPC Lookup", path: PATH },
];

export const Route = createFileRoute("/upc-lookup")({
  component: UpcLookupPage,
  head: () => {
    const base = pageHead({ title: TITLE, description: DESC, path: PATH });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode UPC Lookup",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function UpcLookupPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="UPC Lookup & Validator"
      description="Validate UPC-A and UPC-E codes, verify the check digit and decode the number system. Product-name lookup links out to external catalogs. When the check digit is wrong, the result shows the digit the first 11 numbers require, and a valid UPC-E is expanded to its full UPC-A and EAN-13."
      crumbs={CRUMBS}
      content={
        <>
          <DataAvailabilityNote />
          <NumberSystemTable />
          <UpcAvsUpcE />
          <ToolSeoSections
            aboutHeading="About this UPC lookup tool"
            about={
              <>
                <p>
                  This UPC lookup tool validates and decodes UPC-A and UPC-E barcodes — the 12-digit and
                  8-digit codes found on most retail products in the United States and Canada. It checks
                  the check digit using the official mod-10 algorithm, splits the code into its number
                  system, manufacturer and product segments, and shows the equivalent EAN-13 so you can
                  use it anywhere in the world.
                </p>
                <p>
                  The validator works fully in your browser. A real product database lookup is on the
                  roadmap; for now, validated codes can be sent to external catalog searches with one
                  click.
                </p>
              </>
            }
            whenToUse={[
              "Verifying a UPC before listing a product on Amazon, eBay or Shopify.",
              "Catching a mistyped digit in a printed UPC before sending it to a printer.",
              "Confirming whether a code is UPC-A or compressed UPC-E.",
              "Converting a UPC-A to its EAN-13 equivalent for international use.",
              "Decoding a UPC's number system to spot coupons, NDC or random-weight codes.",
            ]}
            howItWorks={[
              {
                title: "Paste the UPC",
                desc: "Type or paste the digits. Spaces and dashes are ignored automatically.",
              },
              {
                title: "Validate",
                desc: "We compute the check digit and confirm the format is UPC-A or UPC-E.",
              },
              {
                title: "See the structure",
                desc: "Number system, manufacturer prefix, product code and check digit are split out.",
              },
              {
                title: "Get the EAN-13",
                desc: "We show the EAN-13 equivalent (UPC-A with a leading zero) for international catalogs.",
              },
              {
                title: "Look it up externally",
                desc: "Open the validated code in Google or a public catalog if you need product info.",
              },
            ]}
            whatToAvoid={[
              "Trusting any code that fails the check-digit test — it's almost certainly wrong.",
              "Expecting every UPC to return a product name; many private-label items aren't in any public database.",
              "Mixing up UPC-A (12 digits) with EAN-13 (13 digits) — the leading zero matters.",
              "Validating a UPC with extra characters — clean spaces, dashes and letters first.",
              "Paying for a third-party lookup before validating the UPC is even structurally correct.",
            ]}
            tips={[
              "Random-weight codes starting with 2 don't have a stable product mapping — they encode price.",
              "If your UPC is on a coupon, it starts with 5 — that's not a product.",
              "Use the EAN-13 form when querying international catalogs.",
              "Save validated codes to a spreadsheet before sending them to a printer.",
              "For book or magazine codes, use the ISBN reader instead of UPC lookup.",
            ]}
          />
          <h2>How to Look up a UPC Number</h2>
          <p>To look up a UPC number, validate it here first, then send the checked code to a catalog. A search for a mistyped number finds the wrong product or nothing.</p>
          <ol>
            <li>Paste the 12-digit UPC-A or 8-digit UPC-E and press Validate.</li>
            <li>If the check fails, retype the code or get the right last digit from the <Link to="/check-digit-calculator">check digit calculator</Link>.</li>
            <li>Use the Google or Barcode Lookup button for a UPC barcode lookup.</li>
          </ol>
          <h3>Where to Find the UPC Code on a Product</h3>
          <p>The UPC is printed under the barcode, usually on the back or bottom of the pack. On a UPC-A, the first and last digits sit outside the bars.</p>
          <h3>UPC Lookup by Product Name</h3>
          <p>This tool works from the barcode number, not the name. Retailer and marketplace listings often show the UPC or GTIN in the product details, and the brand owner can confirm its own codes.</p>
          <h2>UPC-A to UPC-E Conversion</h2>
          <p>Only a UPC-A with number system 0 or 1 and the right run of zeros can be shortened to UPC-E, and each UPC-E expands to exactly one UPC-A. Paste a UPC-E above to see its full form: 01234565 expands to 012345000065.</p>
          <p>Our <Link to="/barcode-guides/upc-vs-ean">guide to UPC vs EAN</Link> explains when a catalog wants the 12-digit or the 13-digit form.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <UpcLookupApp />
    </ToolLayout>
  );
}

type Result =
  | { kind: "idle" }
  | { kind: "error"; reason: string; hint?: string; expected?: number }
  | {
      kind: "ok";
      upcA: string;
      checkDigit: number;
      numberSystem: { digit: string; description: string };
      manufacturer: string;
      product: string;
      expandedFrom?: "UPC-E";
      ean13: string;
    };

function UpcLookupApp() {
  const [value, setValue] = useState("");
  const [submitted, setSubmitted] = useState(false);
  usePrefillValue((v) => {
    setValue(v);
    setSubmitted(true);
  });

  const result = useMemo<Result>(() => {
    if (!submitted) return { kind: "idle" };
    const raw = value.replace(/[\s-]+/g, "");
    if (!raw) return { kind: "error", reason: "Enter a UPC-A (12 digits) or UPC-E (6–8 digits)." };
    if (!/^\d+$/.test(raw)) {
      return {
        kind: "error",
        reason: "UPC codes contain digits only — remove any letters or punctuation.",
      };
    }

    // Try UPC-A first
    if (raw.length === 12) {
      const v = validateUpcA(raw);
      if (!v.ok) {
        const expected = computeCheckDigit(raw.slice(0, 11));
        return {
          kind: "error",
          reason: v.reason,
          expected,
          hint: `If the first 11 digits are correct, the full UPC should be ${raw.slice(0, 11)}${expected}.`,
        };
      }
      return ok(raw);
    }

    // Try UPC-E expansion (6, 7, 8 digits)
    if (raw.length >= 6 && raw.length <= 8) {
      const expanded = expandUpcE(raw);
      if (expanded) {
        const r = ok(expanded);
        if (r.kind === "ok") r.expandedFrom = "UPC-E";
        return r;
      }
      return {
        kind: "error",
        reason:
          "That doesn't look like a valid UPC-E. UPC-E codes must start with number system 0 or 1.",
      };
    }

    return {
      kind: "error",
      reason: `UPC must be 12 digits (UPC-A) or 6–8 digits (UPC-E). You entered ${raw.length}.`,
    };
  }, [submitted, value]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const reset = () => {
    setValue("");
    setSubmitted(false);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
      <Card>
        <CardContent className="p-5 md:p-6">
          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <label htmlFor="upc-input" className="text-sm font-medium">
              UPC code
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="upc-input"
                inputMode="numeric"
                autoComplete="off"
                placeholder="e.g. 036000291452"
                maxLength={20}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setSubmitted(false);
                }}
                aria-invalid={result.kind === "error"}
              />
              <Button type="submit" disabled={!value.trim()}>
                <Search className="mr-2 h-4 w-4" /> Validate
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Accepts UPC-A (12 digits) or UPC-E (6–8 digits). Spaces and dashes are ignored.
            </p>
            <div className="flex flex-wrap gap-2 pt-1 text-xs">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => {
                    setValue(ex);
                    setSubmitted(true);
                  }}
                  className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
                >
                  {ex}
                </button>
              ))}
            </div>
            <div>
              <Button type="button" size="sm" variant="ghost" onClick={reset}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" /> Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <ResultPanel result={result} />
    </div>
  );
}

const EXAMPLES = ["036000291452", "012345678905", "01234565"];

function ok(upcA: string): Result {
  return {
    kind: "ok",
    upcA,
    checkDigit: Number(upcA[11]),
    numberSystem: upcANumberSystem(upcA),
    manufacturer: upcA.slice(1, 6),
    product: upcA.slice(6, 11),
    ean13: `0${upcA}`,
  };
}

function ResultPanel({ result }: { result: Result }) {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <Card>
      <CardContent className="flex h-full flex-col p-5 md:p-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Result
        </p>
        <div className="mt-4 flex-1">
          {result.kind === "idle" && (
            <EmptyState>
              Enter a UPC and press Validate. We'll verify the check digit and show the structure
              breakdown.
            </EmptyState>
          )}

          {result.kind === "error" && (
            <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Invalid UPC</span>
              </div>
              <p className="text-sm text-muted-foreground">{result.reason}</p>
              {result.hint && (
                <p className="rounded-md border border-border bg-background p-3 text-sm">
                  <Info className="mr-2 inline h-3.5 w-3.5 text-primary" />
                  {result.hint}
                </p>
              )}
            </div>
          )}

          {result.kind === "ok" && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm font-medium">Valid UPC-A</span>
                {result.expandedFrom && (
                  <Badge variant="outline" className="text-xs">
                    Expanded from {result.expandedFrom}
                  </Badge>
                )}
              </div>
              <Row label="UPC-A" value={result.upcA} mono onCopy={copy} />
              <Row label="EAN-13 equivalent" value={result.ean13} mono onCopy={copy} />
              <div className="grid grid-cols-3 gap-2 rounded-lg border border-border bg-background p-3 text-center font-mono text-sm">
                <Segment label="Number system" value={result.upcA[0]} />
                <Segment label="Manufacturer" value={result.manufacturer} />
                <Segment label="Product" value={result.product} />
              </div>
              <div className="text-xs text-muted-foreground">
                Check digit <span className="font-mono text-foreground">{result.checkDigit}</span> —
                matches. Number system{" "}
                <span className="font-mono text-foreground">{result.numberSystem.digit}</span>:{" "}
                {result.numberSystem.description}.
              </div>

              <ProductLookupArea upcA={result.upcA} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ProductLookupArea({ upcA }: { upcA: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4">
      <div className="flex items-center gap-2">
        <PackageSearch className="h-4 w-4 text-primary" aria-hidden />
        <p className="text-sm font-semibold">Product lookup</p>
        <Badge variant="outline" className="text-xs">
          Coming soon
        </Badge>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        ReadBarcode's product database integration is on the roadmap. Lookup results depend on
        what's published in public catalogs — many private-label and regional products aren't listed
        anywhere.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button asChild size="sm" variant="outline">
          <a
            href={`https://www.google.com/search?q=${encodeURIComponent("UPC " + upcA)}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Search Google for this UPC
          </a>
        </Button>
        <Button asChild size="sm" variant="outline">
          <a
            href={`https://www.barcodelookup.com/${encodeURIComponent(upcA)}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Try Barcode Lookup
          </a>
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        External links open in a new tab. ReadBarcode doesn't endorse third-party databases — verify
        before relying on any result.
      </p>
    </div>
  );
}

function Segment({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-base">{value}</div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  onCopy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  onCopy: (s: string) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-3">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`mt-0.5 break-all text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onCopy(value)} aria-label={`Copy ${label}`}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
      <ScanBarcode className="h-7 w-7 text-muted-foreground" aria-hidden />
      <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function DataAvailabilityNote() {
  return (
    <>
      <h2>About Lookup Data</h2>
      <p>
        A UPC is just an identifier — there is no single official database that maps every UPC to a
        product name. Public databases vary in coverage, especially for private-label, regional or
        older items. ReadBarcode's validator works <strong>100% offline in your browser</strong> and
        does not depend on any paid API. The product lookup integration is on the roadmap.
      </p>
    </>
  );
}

function UpcAvsUpcE() {
  const rows = [
    { feature: "Length", a: "12 digits", e: "8 digits (zero-suppressed)" },
    {
      feature: "Typical use",
      a: "Standard retail packaging",
      e: "Small items where a full UPC won't fit",
    },
    {
      feature: "Encodes",
      a: "Number system + manufacturer + product + check digit",
      e: "A compressed form of a UPC-A with specific zero patterns",
    },
    { feature: "Scans as", a: "UPC-A", e: "Expands back to its 12-digit UPC-A at the register" },
  ];
  return (
    <>
      <h2>UPC-A vs UPC-E</h2>
      <p>
        UPC-E is not a different product code — it's a shorter printing of a UPC-A that drops runs
        of zeros so the barcode fits on small packs. This tool validates both and shows the expanded
        12-digit form when you paste a UPC-E.
      </p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col"></th>
              <th scope="col">UPC-A</th>
              <th scope="col">UPC-E</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.feature}>
                <td>{r.feature}</td>
                <td>{r.a}</td>
                <td>{r.e}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

function NumberSystemTable() {
  const rows = [
    { d: "0, 1, 6, 7, 8, 9", desc: "Regular UPC codes (most retail products)" },
    { d: "2", desc: "Random-weight items (produce, meat, deli) — price encoded in the barcode" },
    { d: "3", desc: "National Drug Code (NDC) — pharmacy and OTC drugs" },
    { d: "4", desc: "Loyalty cards and in-store use" },
    { d: "5", desc: "Coupons" },
  ];
  return (
    <>
      <h2>UPC-A number system digit</h2>
      <p>
        The first digit of a UPC-A hints at what kind of code it is. This is a coarse classification
        — manufacturers and use cases overlap.
      </p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Digit</th>
              <th scope="col">Meaning</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.d}>
                <td>
                  <code>{r.d}</code>
                </td>
                <td>{r.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}
function RelatedTools() {
  const related = [
    {
      path: "/ean-lookup",
      label: "EAN Lookup",
      desc: "Validate EAN-8 and EAN-13 barcodes used worldwide.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify GTIN-8/12/13/14 check digits.",
    },
    {
      path: "/check-digit-calculator",
      label: "Check Digit Calculator",
      desc: "Compute the correct UPC/EAN/GTIN check digit.",
    },
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Scan a UPC with your camera or an image.",
    },
    {
      path: "/barcode-guides/upc-vs-ean",
      label: "UPC vs EAN Guide",
      desc: "When to use the 12-digit UPC-A or the 13-digit EAN-13.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group flex items-start gap-3 rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <ShieldCheck className="mt-0.5 h-4 w-4 text-primary" />
            <div>
              <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
              <div className="text-xs text-muted-foreground">{r.desc}</div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
